import { normalizeTrailingSlash } from '@/shared/lib/url-utils';

export type PathWithSlash = `/${string}`;

/**
 * Creates an endpoint builder that ensures proper path concatenation
 * @param basePrefix - The base prefix for the API (e.g., '/auth', '/api/v1/users')
 * @returns A function that appends paths to the base prefix safely
 *
 * @example
 * ```ts
 * const authEndpoint = createEndpointBuilder('/auth');
 * authEndpoint('/login'); // Returns: '/auth/login'
 * authEndpoint('/me'); // Returns: '/auth/me'
 * authEndpoint(''); // Returns: '/auth'
 *
 * const apiEndpoint = createEndpointBuilder('/api/v1/users');
 * apiEndpoint('/123'); // Returns: '/api/v1/users/123'
 * apiEndpoint('/123/profile'); // Returns: '/api/v1/users/123/profile'
 * ```
 */
export function createEndpointBuilder(basePrefix: PathWithSlash) {
  const normalizedBase = normalizeTrailingSlash(basePrefix);

  /**
   * Builds a complete endpoint path
   * @param path - The path to append (must start with /)
   * @returns The complete endpoint URL
   */
  return function buildEndpoint(path: PathWithSlash | ''): string {
    if (!path) {
      return normalizedBase;
    }

    return `${normalizedBase}${path}`;
  };
}
